import React from "react";
import { ChangeEvent, FormEvent } from "react";
import ReactModal from 'react-modal';

import { V2 } from "../Api";
import { IAuthProps } from "./AuthContext";
import withAuth from "./withAuth";

import MenuItem from "./MenuItem";
import UserInfoDisplay from "./UserInfoDisplay";
import VerticalMenu from "./VerticalMenu";

interface IUserInfoControlsState {
    isModalOpen: boolean;

    currentPassword: string;
    newPassword1: string;
    newPassword2: string;

    isPasswordValid: boolean;

    submitErrorMessage?: string | boolean;

    [name: string]: string | boolean | number | undefined;
}

class UserInfoControls extends React.Component<IAuthProps, IUserInfoControlsState> {
    constructor(props: Readonly<IAuthProps>) {
        super(props);

        this.state = {
            isModalOpen: false,

            currentPassword: "",
            newPassword1: "",
            newPassword2: "",

            isPasswordValid: false,
        };

        this.logOutUser = this.logOutUser.bind(this);
        this.showPasswordChange = this.showPasswordChange.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.doPasswordChange = this.doPasswordChange.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<IAuthProps>, prevState: Readonly<IUserInfoControlsState>) {
        if (prevState.currentPassword !== this.state.currentPassword ||
            prevState.newPassword1 !== this.state.newPassword1 ||
            prevState.newPassword2 !== this.state.newPassword2) {

            const isPasswordValid =
                (!!this.state.currentPassword) &&
                (!!this.state.newPassword1 && this.state.newPassword1.length >= 7) &&
                (!!this.state.newPassword2 && this.state.newPassword2.length >= 7);

            this.setState({
                isPasswordValid
            });
        }
    }

    public render() {
        const header = (
            <div className="user-display-top" tabIndex={0} role="button">
                <UserInfoDisplay />
                <i className="icon-chevron-down" />
            </div>
        );

        return (
            <VerticalMenu className="user-display-bottom">
                {
                    this.state.isModalOpen
                        ? <ReactModal
                            isOpen={this.state.isModalOpen} onRequestClose={this.closeDialog}
                            overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal-password"
                            bodyOpenClassName="modal-password-open"
                            contentLabel="Change Password">
                            <form className="input-form" onSubmit={this.doPasswordChange}>
                                <section className="info-block">
                                    <header>
                                        <h3>Change Password</h3>
                                    </header>

                                    <p>To set a new password, enter your current password:</p>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-dialog-current-password">Current Password</label>
                                            <input type="password" id="frm-dialog-nurse" name="currentPassword" value={this.state.currentPassword} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                        </div>
                                    </section>

                                    <p>Now, enter your new password twice to make sure you get it right:</p>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-dialog-new-password-1">New Password</label>
                                            <input type="password" id="frm-dialog-new-password-1" name="newPassword1" value={this.state.newPassword1} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                        </div>
                                    </section>

                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-dialog-new-password-2">Confirm New Password</label>
                                            <input type="password" id="frm-dialog-new-password-2" name="newPassword2" value={this.state.newPassword2} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                        </div>
                                    </section>

                                    <p className="small-text">Changing your password will log you out of everywhere you are logged in.</p>
                                    {
                                        this.state.submitErrorMessage === true
                                            ? <div className="spinner lds-dual-ring" />
                                            : this.state.submitErrorMessage
                                                ? <div className="error-box">{this.state.submitErrorMessage}</div>
                                                : ""
                                    }
                                    <div className="action-panel-dialog">
                                        <button type="submit" className="button push-right text-with-icon" disabled={!this.state.isPasswordValid || this.state.submitErrorMessage === true}><i className="icon-lock" /><span>Change It</span></button>
                                        <button type="button" className="button" onClick={this.closeDialog}>Cancel</button>
                                    </div>
                                </section>
                            </form>
                        </ReactModal>
                        : undefined
                }
                <MenuItem header={header} className="user-display-top-item">
                    <MenuItem header="Your Bonuses" link="/bonus" match={["/bonus", "/bonus/:date(\\d+-\\d+)"]} rights="bonus:own:read" exact={true} />
                    <MenuItem header="Your Assessments" link="/assessment" match={["/assessment", "/assessment/:id"]} rights="assess:read:self" exact={true} />
                    <MenuItem header="Change Password" onClick={this.showPasswordChange} />
                    <MenuItem header="Log out" onClick={this.logOutUser} />
                </MenuItem>
            </VerticalMenu>
        );
    }

    private logOutUser() {
        this.props.authContext.logoutUser();
    }

    private showPasswordChange() {
        this.setState({
            isModalOpen: true,
        });
    }

    private closeDialog() {
        this.setState({
            isModalOpen: false,

            currentPassword: "",
            newPassword1: "",
            newPassword2: "",
            submitErrorMessage: ""
        });
    }

    private async doPasswordChange(e: FormEvent) {
        e.preventDefault();
        if (!this.state.currentPassword || !this.state.newPassword1 || !this.state.newPassword2) {
            return;
        }

        if (this.state.newPassword1 !== this.state.newPassword2) {
            this.setState({
                submitErrorMessage: "New passwords must match"
            });
            return;
        }

        if (this.state.currentPassword === this.state.newPassword1) {
            this.setState({
                submitErrorMessage: "New password must be different than current password"
            });
            return;
        }

        try {
            this.setState({
                submitErrorMessage: true,
            });

            const passwordRequest = {
                currentPassword: this.state.currentPassword,
                newPassword: this.state.newPassword1
            };

            await V2.post("auth/set_password", passwordRequest);

            // Clear login info
            await this.props.authContext.logoutUser();
        }
        catch (e) {
            this.setState({
                submitErrorMessage: e.statusCode === 403 ? "Current Password Incorrect" : e.message
            });
        }
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        if (e.currentTarget.type === "number") {
            this.setState({
                [name]: parseInt(value, 10)
            });
        }
        else {
            this.setState({
                [name]: value
            });
        }
    }
}

export default withAuth(UserInfoControls);
